import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

// Create Apollo Client
// Use relative URL when REACT_APP_API_URL unset (production: same-origin, token proxy forwards to backend)
const apiBase = process.env.REACT_APP_API_URL ?? '';
const graphqlUri = apiBase ? `${apiBase.replace(/\/$/, '')}/graphql/` : '/graphql/';
const httpLink = createHttpLink({
  uri: graphqlUri,
  credentials: 'include', // Include cookies for session-based auth
});

const authLink = setContext(async (_, { headers }) => {
  // Get CSRF token from cookie
  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return '';
  };

  const csrfToken = getCookie('csrftoken');
  
  return {
    headers: {
      ...headers,
      ...(csrfToken && { 'X-CSRFToken': csrfToken }),
    }
  }
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

// Create Material-UI theme with 2026 Style Guide - Option 1 palette
// Navy, Teal, Sky Blue, Emerald color scheme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1e3a5f', // Deep navy blue
      light: '#2d4a6f', // Lighter navy
      dark: '#152a4a', // Darker navy
    },
    secondary: {
      main: '#0891b2', // Teal/Cyan
      light: '#22d3ee', // Light cyan
      dark: '#0e7490', // Dark cyan
    },
    background: {
      default: '#f0f9ff', // Light sky blue tint
      paper: '#FFFFFF',
    },
    error: {
      main: '#dc2626', // Red-600
      light: '#ef4444',
      dark: '#b91c1c',
    },
    warning: {
      main: '#f59e0b', // Amber-500
      light: '#fbbf24',
      dark: '#d97706',
    },
    success: {
      main: '#22c55e', // Emerald green
      light: '#4ade80',
      dark: '#16a34a',
    },
    info: {
      main: '#0891b2', // Teal/Cyan (matches secondary)
      light: '#22d3ee',
      dark: '#0e7490',
    },
    text: {
      primary: '#1e293b', // Slate-800 (dark charcoal)
      secondary: '#475569', // Slate-600
    },
  },
  typography: {
    // 2026 Style Guide typography - clean, professional with bold navy headings
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // H1 - Page Titles / Main Headers (Bold navy)
    h1: {
      fontSize: '2.5rem',    // 40px - Main page titles
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: '#1e3a5f',      // Deep navy
    },
    // H2 - Section Headers (Bold, slightly lighter navy)
    h2: {
      fontSize: '1.875rem',  // 30px - Major section headers
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: '#1e3a5f',      // Deep navy
    },
    // H3 - Card/Module Headers (Bold, can be uppercase for feature charts)
    h3: {
      fontSize: '1.5rem',    // 24px - Card titles, prominent stats
      fontWeight: 700,
      lineHeight: 1.4,
      color: '#1e3a5f',      // Deep navy
    },
    // H4 - Detail Heading/Caption (Uppercase, for charts and graphs)
    h4: {
      fontSize: '0.875rem',  // 14px - Detail headings, chart labels
      fontWeight: 700,
      lineHeight: 1.5,
      letterSpacing: '0.05em',
      textTransform: 'uppercase' as const,
      color: '#0891b2',      // Teal accent
    },
    // H5 - Small Headers / Emphasized Text
    h5: {
      fontSize: '1.125rem',  // 18px - Small headers, list titles
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#1e293b',
    },
    // H6 - Table Headers / Tab Labels
    h6: {
      fontSize: '1rem',      // 16px - Table headers, tabs, chips
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#1e293b',
    },
    // Subtitle 1 - Lead text (larger intro text)
    subtitle1: {
      fontSize: '1.125rem',  // 18px - Lead/intro text
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.00938em',
      color: '#475569',
    },
    // Subtitle 2 - Secondary descriptions
    subtitle2: {
      fontSize: '0.875rem',  // 14px - Smaller subtitles
      fontWeight: 500,
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
      color: '#475569',
    },
    // Body 1 - Primary body text
    body1: {
      fontSize: '1rem',      // 16px - Primary body text
      fontWeight: 400,
      lineHeight: 1.7,
      letterSpacing: '0.00938em',
      color: '#1e293b',
    },
    // Body 2 - Secondary body text
    body2: {
      fontSize: '0.875rem',  // 14px - Secondary body text, descriptions
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.01071em',
      color: '#475569',
    },
    // Caption - Small text
    caption: {
      fontSize: '0.75rem',   // 12px - Captions, timestamps, metadata
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.03333em',
      color: '#64748b',
    },
    // Overline - Dimension/Callout (uppercase accent)
    overline: {
      fontSize: '0.6875rem', // 11px - Dimension, callout labels
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: '0.1em',
      textTransform: 'uppercase' as const,
      color: '#0891b2',      // Teal accent
    },
    // Button text
    button: {
      fontSize: '0.875rem',  // 14px - Button text
      fontWeight: 600,
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'none' as const,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '10px 22px',
          boxShadow: 'none',
          letterSpacing: '0.02857em',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(30, 58, 95, 0.2)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          backgroundColor: '#1e3a5f',
          '&:hover': {
            backgroundColor: '#2d4a6f',
          },
        },
        containedSecondary: {
          backgroundColor: '#0891b2',
          '&:hover': {
            backgroundColor: '#0e7490',
          },
        },
        outlinedPrimary: {
          borderColor: '#1e3a5f',
          color: '#1e3a5f',
          '&:hover': {
            backgroundColor: 'rgba(30, 58, 95, 0.04)',
            borderColor: '#1e3a5f',
          },
        },
        sizeLarge: {
          fontSize: '1rem',
          padding: '14px 32px',
        },
        sizeSmall: {
          fontSize: '0.8125rem',
          padding: '6px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(30, 58, 95, 0.08)',
          border: '1px solid rgba(30, 58, 95, 0.08)',
          transition: 'all 0.2s ease-in-out',
          background: '#ffffff',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(30, 58, 95, 0.12), 0 4px 8px rgba(0,0,0,0.04)',
            transform: 'translateY(-2px)',
            border: '1px solid rgba(30, 58, 95, 0.12)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '1.25rem',
          fontWeight: 700,
          lineHeight: 1.4,
          color: '#1e3a5f',
        },
        subheader: {
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1.6,
          color: '#475569',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: '0.8125rem',
          letterSpacing: '0.02em',
        },
        sizeSmall: {
          fontSize: '0.75rem',
          fontWeight: 600,
        },
        colorPrimary: {
          backgroundColor: '#1e3a5f',
        },
        colorSecondary: {
          backgroundColor: '#0891b2',
        },
        colorSuccess: {
          backgroundColor: '#22c55e',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.9375rem',
          fontWeight: 600,
          letterSpacing: '0.01em',
          minHeight: 48,
          color: '#475569',
          '&.Mui-selected': {
            fontWeight: 700,
            color: '#1e3a5f',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#0891b2',
          height: 3,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: '#0891b2',
          borderBottom: '2px solid #e2e8f0',
        },
        body: {
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1.6,
          color: '#1e293b',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
        selectLabel: {
          fontSize: '0.875rem',
          fontWeight: 400,
        },
        displayedRows: {
          fontSize: '0.875rem',
          fontWeight: 400,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 500,
          letterSpacing: '0.00714em',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: 1.5,
        },
        input: {
          fontSize: '1rem',
          fontWeight: 400,
          '&::placeholder': {
            fontSize: '0.875rem',
            fontWeight: 400,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1.6,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '1rem',
          fontWeight: 500,
          lineHeight: 1.5,
        },
        secondary: {
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1.6,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        message: {
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: 1.6,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: 1.5,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 4px rgba(30, 58, 95, 0.08)',
          borderBottom: '1px solid rgba(30, 58, 95, 0.1)',
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255, 255, 255, 0.97)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e3a5f',
          color: '#ffffff',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: '#e2e8f0',
        },
        barColorPrimary: {
          backgroundColor: '#0891b2',
        },
        barColorSecondary: {
          backgroundColor: '#22c55e',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(30, 58, 95, 0.1)',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'h6',
          subtitle2: 'h6',
          body1: 'p',
          body2: 'p',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </ApolloProvider>
  </React.StrictMode>
);
